import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import AtmMachine from '../components/atm/AtmMachine';
import Layout from '../components/layout/Layout';
import { useAtm } from '../context/AtmContext';
import { Atm, SharedAtm, SharedAtmTransaction } from '../types/atm';

const SharedAtmView: React.FC = () => {
  const { shareId, accessCode } = useParams<{ shareId: string; accessCode?: string }>();
  const { fetchSharedAtm, processTransaction } = useAtm();

  const [sharedAtm, setSharedAtm] = useState<SharedAtm | null>(null);
  const [parentAtm, setParentAtm] = useState<Atm | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<SharedAtmTransaction[]>([]);

  // Load shared ATM data
  useEffect(() => {
    const loadSharedAtm = async () => {
      if (!shareId) {
        setError('Invalid share link');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('🔗 SharedAtmView: Loading shared ATM with ID:', shareId);

        const sharedAtmData = await fetchSharedAtm(shareId);

        if (!sharedAtmData) {
          console.log('❌ SharedAtmView: Shared ATM not found');
          setError('Shared ATM not found');
          setLoading(false);
          return;
        }

        console.log('✅ SharedAtmView: Shared ATM loaded:', sharedAtmData);
        setSharedAtm(sharedAtmData);
        setTransactions(sharedAtmData.transactions);

        // Build ATM from the shared ATM data (includes all options, theme, settings)
        console.log('🏗️ SharedAtmView: Building ATM from shared data');
        const atmFromSharedData: Atm = {
          id: sharedAtmData.atmId,
          name: sharedAtmData.atmName,
          description: sharedAtmData.description || `Shared ATM: ${sharedAtmData.atmName}`,
          createdBy: sharedAtmData.ownerId,
          createdByName: sharedAtmData.ownerName,
          createdAt: sharedAtmData.createdAt,
          updatedAt: sharedAtmData.updatedAt,
          options: sharedAtmData.options,
          theme: sharedAtmData.theme,
          atmSettings: sharedAtmData.atmSettings,
          isPublic: false,
          transactionCount: sharedAtmData.transactionCount,
          transactions: [],
        };

        console.log(
          '✅ SharedAtmView: ATM constructed with',
          atmFromSharedData.options.length,
          'options'
        );
        setParentAtm(atmFromSharedData);
      } catch (err) {
        console.error('💥 SharedAtmView: Error loading shared ATM:', err);
        setError('Failed to load shared ATM');
      } finally {
        setLoading(false);
      }
    };

    loadSharedAtm();
  }, [shareId, fetchSharedAtm]);

  // Handle transaction on shared ATM
  const handleTransaction = async (transaction: any) => {
    if (!sharedAtm || !shareId) return;

    try {
      // Create shared ATM transaction
      const sharedTransaction: SharedAtmTransaction = {
        id: transaction.id,
        shareId: shareId,
        optionId: transaction.optionId,
        optionText: transaction.optionText,
        type: transaction.type,
        amount: transaction.amount,
        timestamp: transaction.timestamp,
        userId: transaction.userId,
        status: transaction.status,
        message: transaction.message,
      };

      // Process the transaction
      await processTransaction(shareId, sharedTransaction);

      // Update local state
      setTransactions(prev => [...prev, sharedTransaction]);
      setSharedAtm(prev =>
        prev
          ? {
              ...prev,
              transactionCount: prev.transactionCount + 1,
              transactions: [...prev.transactions, sharedTransaction],
            }
          : null
      );
    } catch (err) {
      console.error('Error processing shared ATM transaction:', err);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="mt-4 text-gray-600">Loading shared ATM...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !sharedAtm || !parentAtm) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-12 h-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Shared ATM Not Found</h3>
            <p className="text-gray-600 mb-6">
              This shared ATM may have been deleted or the link might be incorrect.
            </p>
            <Link
              to="/explore-atms"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              Explore ATMs
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  // Check if ATM has expired or reached limits
  const isExpired =
    sharedAtm.shareSettings.expiresAt &&
    new Date(sharedAtm.shareSettings.expiresAt as any) < new Date();

  const hasReachedLimit =
    sharedAtm.shareSettings.maxTransactions &&
    sharedAtm.transactionCount >= sharedAtm.shareSettings.maxTransactions;

  if (isExpired || hasReachedLimit || !sharedAtm.isActive) {
    return (
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-12 h-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {isExpired
                ? 'Shared ATM Expired'
                : hasReachedLimit
                  ? 'Transaction Limit Reached'
                  : 'Shared ATM Inactive'}
            </h3>
            <p className="text-gray-600 mb-6">
              This shared ATM is no longer available for transactions.
            </p>
            <Link
              to="/explore-atms"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              Explore ATMs
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <Link
                to="/explore-atms"
                className="text-blue-600 hover:text-blue-700 flex items-center"
              >
                <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Back to ATMs
              </Link>
            </div>
          </div>

          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{sharedAtm.atmName}</h1>
            <p className="text-gray-600 mb-4">Shared by {sharedAtm.ownerName}</p>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full mr-2 bg-green-500"></div>
                Shared ATM
              </div>
              <div>
                {sharedAtm.transactionCount} transaction
                {sharedAtm.transactionCount !== 1 ? 's' : ''}
                {sharedAtm.shareSettings.maxTransactions &&
                  ` / ${sharedAtm.shareSettings.maxTransactions} limit`}
              </div>
              {sharedAtm.shareSettings.expiresAt && (
                <div>
                  Expires: {new Date(sharedAtm.shareSettings.expiresAt as any).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ATM Machine */}
        <div className="flex justify-center">
          <AtmMachine atm={parentAtm} onTransaction={handleTransaction} disabled={false} />
        </div>

        {/* Call to Action */}
        <div className="mt-8 text-center">
          <div className="bg-blue-50 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Create Your Own ATM</h3>
            <p className="text-gray-600 mb-4">
              Want to create your own interactive ATM? Get started now and customize your
              experience.
            </p>
            <Link
              to="/create-atm"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Create ATM
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SharedAtmView;
